<template>
  <div class="na-page na-staff">
    <StaffHeader role="Staff" />

    <main class="st-wrap st-below">
      <div class="st-head">
        <div>
          <span class="st-kicker">New customer</span>
          <h1 class="st-title">Onboard a town or publication</h1>
          <p class="st-sub">Creates their login, their customer record and their {{ TRIAL_DAYS }}-day trial, and emails them a link to set a password. You stay signed in as yourself.</p>
        </div>
        <router-link v-if="leadId" :to="`/staff-leads?lead=${leadId}`" class="st-btn outline"><v-icon size="18">mdi-account-search-outline</v-icon>Back to lead</router-link>
      </div>

      <div v-if="leadName" class="st-note ok lead-note">Filled in from your lead <b>{{ leadName }}</b>. The lead is marked <b>Won</b> (+50 points) when you create the account.</div>

      <div class="layout">
        <section class="st-card wizard">
          <ol class="steps">
            <li v-for="(s, i) in steps" :key="s" :class="{ on: step === i, done: i < step }">
              <button type="button" :disabled="i > maxStep" @click="step = i">
                <span class="n"><v-icon v-if="i < step" size="16" color="#fff">mdi-check</v-icon><template v-else>{{ i + 1 }}</template></span>{{ s }}
              </button>
            </li>
          </ol>

          <!-- 1. Account -->
          <div v-if="step === 0" class="st-stack">
            <h2 class="panel">Sign-in for the main contact</h2>
            <div class="st-field">
              <label for="ob-email">Email *</label>
              <input id="ob-email" v-model.trim="f.email" type="email" placeholder="clerk@yourtown.gov" autocomplete="off" />
            </div>
            <div class="st-field">
              <label for="ob-pass">Temporary password *</label>
              <div class="pass">
                <input id="ob-pass" v-model="f.password" :type="showPwd ? 'text' : 'password'" placeholder="At least 8 characters" autocomplete="new-password" />
                <button type="button" class="st-icon-btn" :title="showPwd ? 'Hide' : 'Show'" @click="showPwd = !showPwd"><v-icon size="18">{{ showPwd ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon></button>
                <button type="button" class="st-btn outline sm" @click="genPassword">Generate</button>
              </div>
              <div class="st-bar"><div :style="{ width: strength + '%' }"></div></div>
              <small class="st-muted">Strength: {{ strengthLabel }}. They'll get an email to choose their own password, so they never need this one.</small>
            </div>
          </div>

          <!-- 2. Contact -->
          <div v-if="step === 1" class="st-stack">
            <h2 class="panel">Main contact</h2>
            <div class="st-grid-2">
              <div class="st-field"><label for="ob-name">Full name</label><input id="ob-name" v-model="f.fullName" placeholder="Dana Ruiz" /></div>
              <div class="st-field">
                <label for="ob-title">Title</label>
                <select id="ob-title" v-model="f.contactTitle">
                  <option value="">Choose…</option>
                  <optgroup v-for="g in CONTACT_ROLES" :key="g.group" :label="g.group">
                    <option v-for="r in g.roles" :key="r" :value="r">{{ r }}</option>
                  </optgroup>
                </select>
              </div>
            </div>
            <div class="st-grid-2">
              <div class="st-field"><label for="ob-phone">Phone</label><input id="ob-phone" v-model="f.phone" type="tel" placeholder="(555) 123-4567" /></div>
              <div class="st-field"><label for="ob-web">Website</label><input id="ob-web" v-model="f.website" type="url" placeholder="https://yourtown.gov" /></div>
            </div>
          </div>

          <!-- 3. Organization -->
          <div v-if="step === 2" class="st-stack">
            <h2 class="panel">Town or publication</h2>
            <div class="st-field"><label for="ob-org">Name *</label><input id="ob-org" v-model="f.businessName" placeholder="Town of Springfield" /></div>
            <div class="st-grid-2">
              <div class="st-field">
                <label for="ob-type">Type</label>
                <select id="ob-type" v-model="f.orgType"><option v-for="o in ORG_TYPES" :key="o" :value="o">{{ o }}</option></select>
              </div>
              <div class="st-field">
                <label for="ob-pop">Population</label>
                <select id="ob-pop" v-model="f.population" @change="suggestPlan"><option value="">Choose…</option><option v-for="p in POPULATIONS" :key="p.label" :value="p.label">{{ p.label }}</option></select>
              </div>
            </div>
            <div class="st-field">
              <label for="ob-pub">App / publication name</label>
              <input id="ob-pub" v-model="f.publicationName" placeholder="The Springfield Gazette" />
              <small class="st-muted">The name residents will see on the app icon and newsletters. Leave blank to use the town's name.</small>
            </div>
            <div class="st-field"><label for="ob-desc">Notes for the build team</label><textarea id="ob-desc" v-model="f.businessDescription" rows="3" placeholder="Sections they care about, existing newsletter, who approves posts, launch date they mentioned…"></textarea></div>
          </div>

          <!-- 4. Address -->
          <div v-if="step === 3" class="st-stack">
            <h2 class="panel">Mailing address</h2>
            <div class="st-field"><label for="ob-street">Street *</label><input id="ob-street" v-model="f.address.street" placeholder="1 Town Hall Square" /></div>
            <div class="st-grid-3">
              <div class="st-field"><label for="ob-city">City / town *</label><input id="ob-city" v-model="f.address.city" /></div>
              <div class="st-field"><label for="ob-state">State *</label><input id="ob-state" v-model="f.address.state" maxlength="2" placeholder="CT" @input="f.address.state = f.address.state.toUpperCase()" /></div>
              <div class="st-field"><label for="ob-zip">ZIP *</label><input id="ob-zip" v-model="f.address.zip" inputmode="numeric" maxlength="10" /></div>
            </div>
          </div>

          <!-- 5. Plan & billing -->
          <div v-if="step === 4" class="st-stack">
            <h2 class="panel">Plan &amp; billing</h2>
            <div class="plans">
              <button v-for="p in PLAN_LIST" :key="p.key" type="button" class="plan" :class="{ on: f.plan === p.key }" @click="f.plan = p.key">
                <b>{{ p.name }}</b>
                <span class="price">{{ p.from ? 'from ' : '' }}{{ money(f.billingInterval === 'year' ? p.yearly : p.monthly) }}<small>/{{ f.billingInterval === 'year' ? 'yr' : 'mo' }}</small></span>
                <small>{{ p.fits }} · {{ p.emails.toLocaleString() }} emails/mo</small>
              </button>
            </div>
            <div v-if="f.plan === 'city'" class="st-field">
              <label for="ob-custom">City monthly price (optional)</label>
              <input id="ob-custom" v-model.number="f.customMonthly" type="number" min="1499" step="1" placeholder="1499" />
              <small class="st-muted">Leave blank for $1,499/mo. Yearly is 10× the monthly price.</small>
            </div>
            <div class="st-grid-2">
              <div class="st-field">
                <label>Billing period</label>
                <div class="st-seg"><button type="button" :class="{ on: f.billingInterval === 'month' }" @click="f.billingInterval = 'month'">Monthly</button><button type="button" :class="{ on: f.billingInterval === 'year' }" @click="f.billingInterval = 'year'">Yearly — no setup fee</button></div>
              </div>
              <div class="st-field">
                <label>How they'll pay</label>
                <div class="st-seg"><button type="button" :class="{ on: f.paymentMethod === 'card' }" @click="f.paymentMethod = 'card'">Card</button><button type="button" :class="{ on: f.paymentMethod === 'invoice' }" @click="f.paymentMethod = 'invoice'">Invoice (net 30)</button></div>
              </div>
            </div>

            <div class="st-rows summary">
              <div class="st-row"><span>{{ PLANS[f.plan].name }} plan</span><b>{{ money(amount) }}/{{ f.billingInterval === 'year' ? 'year' : 'month' }}</b></div>
              <div class="st-row"><span>Setup fee</span><b>{{ setupFee ? money(setupFee) + ' on first invoice' : 'Waived (yearly)' }}</b></div>
              <div class="st-row"><span>Free trial</span><b>{{ TRIAL_DAYS }} days · up to {{ TRIAL_EMAIL_CAP.toLocaleString() }} emails</b></div>
            </div>

            <template v-if="f.paymentMethod === 'invoice'">
              <div class="st-grid-2">
                <div class="st-field"><label for="ob-bname">Accounts payable contact</label><input id="ob-bname" v-model="f.billingName" placeholder="Finance Office" /></div>
                <div class="st-field"><label for="ob-bemail">Invoice email</label><input id="ob-bemail" v-model.trim="f.billingEmail" type="email" placeholder="finance@yourtown.gov" /></div>
              </div>
              <div class="st-field"><label for="ob-po">PO number (if they use one)</label><input id="ob-po" v-model="f.poNumber" maxlength="30" /></div>
              <div class="st-note">Stripe emails a net-30 invoice each billing period. Towns can pay by ACH, card or check. No card is needed now.</div>
            </template>

            <template v-else>
              <div class="st-field"><label for="ob-cname">Name on card</label><input id="ob-cname" v-model="f.billingName" /></div>
              <div v-if="cardSaved" class="st-note ok card-saved">
                <span><v-icon size="18" color="#2F7D46">mdi-check-circle</v-icon> {{ cardBrand }} •••• {{ cardLast4 }} saved</span>
                <button type="button" class="st-link" @click="changeCard">Change</button>
              </div>
              <template v-else>
                <div v-if="!cardOpen" class="card-prompt">
                  <button type="button" class="st-btn outline" :disabled="!canStep(0)" @click="openCard"><v-icon size="18">mdi-credit-card-plus-outline</v-icon>Add a card now</button>
                  <small class="st-muted">Optional — they can add it later from their Billing page before the trial ends.</small>
                </div>
                <div v-else class="st-stack">
                  <div v-if="stripeLoading" class="st-muted">Loading secure card form…</div>
                  <div id="ob-card" class="card-el" :class="{ hidden: stripeLoading }"></div>
                  <div class="row-end">
                    <button type="button" class="st-btn ghost sm" @click="closeCard">Cancel</button>
                    <button type="button" class="st-btn primary sm" :disabled="!cardComplete || savingCard" @click="saveCard">{{ savingCard ? 'Saving…' : 'Save card' }}</button>
                  </div>
                </div>
                <div v-if="cardError" class="st-alert">{{ cardError }}</div>
              </template>
              <small class="st-muted">Card details go straight to Stripe. We never see or store the card number.</small>
            </template>

            <details class="more">
              <summary>Languages &amp; extra notes</summary>
              <div class="st-stack">
                <div class="st-field">
                  <label for="ob-lang">Languages the app should support</label>
                  <div class="tags">
                    <span v-for="(l, i) in f.languages" :key="l" class="st-badge navy">{{ l }} <button type="button" class="x" @click="f.languages.splice(i, 1)">×</button></span>
                    <input id="ob-lang" v-model="newLang" placeholder="Add, then press Enter" @keydown.enter.prevent="addLang" />
                  </div>
                </div>
                <div class="st-field"><label for="ob-notes">Additional notes</label><textarea id="ob-notes" v-model="f.additionalNotes" rows="3"></textarea></div>
              </div>
            </details>
          </div>

          <!-- 6. Review -->
          <div v-if="step === 5" class="st-stack">
            <h2 class="panel">Review &amp; create</h2>
            <div class="review">
              <div><h4>Sign-in</h4><p>{{ f.email }}</p></div>
              <div><h4>Contact</h4><p>{{ f.fullName || '—' }}<template v-if="f.contactTitle"><br>{{ f.contactTitle }}</template><template v-if="f.phone"><br>{{ f.phone }}</template></p></div>
              <div><h4>Customer</h4><p>{{ f.businessName }}<br><span class="st-muted">{{ f.orgType }}<template v-if="f.population"> · {{ f.population }}</template></span><template v-if="f.publicationName"><br>App name: {{ f.publicationName }}</template></p></div>
              <div><h4>Address</h4><p>{{ f.address.street }}<br>{{ f.address.city }}, {{ f.address.state }} {{ f.address.zip }}</p></div>
              <div><h4>Plan</h4><p>{{ PLANS[f.plan].name }} — {{ money(amount) }}/{{ f.billingInterval === 'year' ? 'year' : 'month' }}<br><span class="st-muted">Setup fee: {{ setupFee ? money(setupFee) : 'waived' }} · {{ TRIAL_DAYS }}-day trial</span></p></div>
              <div><h4>Payment</h4><p>{{ f.paymentMethod === 'invoice' ? `Invoice, net 30${f.billingEmail ? ' → ' + f.billingEmail : ''}` : cardSaved ? `${cardBrand} •••• ${cardLast4}` : 'Card — to be added later' }}</p></div>
            </div>
            <div v-if="message" :class="['st-note', messageType]">{{ message }}</div>
            <div v-if="error" class="st-alert">{{ error }}</div>
          </div>

          <div class="foot">
            <button v-if="step > 0" type="button" class="st-btn ghost" @click="step--"><v-icon size="18">mdi-arrow-left</v-icon>Back</button>
            <span class="grow"></span>
            <button v-if="step < steps.length - 1" type="button" class="st-btn primary" :disabled="!canStep(step)" @click="next">Continue<v-icon size="18">mdi-arrow-right</v-icon></button>
            <button v-else type="button" class="st-btn primary" :disabled="submitting || done" @click="submit">{{ submitting ? 'Creating…' : done ? 'Created' : 'Create customer account' }}</button>
          </div>
        </section>

        <aside class="st-card side">
          <h3 class="st-card-title"><v-icon size="20">mdi-clipboard-check-outline</v-icon>Checklist</h3>
          <ul class="check">
            <li :class="{ ok: canStep(0) }">Email &amp; temporary password</li>
            <li :class="{ ok: !!f.fullName }">Main contact's name</li>
            <li :class="{ ok: canStep(2) }">Town or publication name</li>
            <li :class="{ ok: canStep(3) }">Mailing address</li>
            <li :class="{ ok: f.paymentMethod === 'invoice' ? !!f.billingEmail : cardSaved }">{{ f.paymentMethod === 'invoice' ? 'Invoice email' : 'Card (optional now)' }}</li>
          </ul>
          <div class="st-note">After creating the account, set their build stage from <router-link to="/manage-app-progress" class="st-link">App progress</router-link>. Each change emails the contact.</div>
        </aside>
      </div>
    </main>
  </div>
</template>

<script>
import StaffHeader from '@/components/StaffHeader.vue'
import { db, functions } from '@/firebase'
import { doc, getDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { loadStripe } from '@stripe/stripe-js/pure' // loads Stripe only when a card form opens (no Stripe cookies on other pages)
import { CONTACT_ROLES, ORG_TYPES, POPULATIONS } from '@/salesConfig'
import { PLANS, PLAN_LIST, TRIAL_DAYS, TRIAL_EMAIL_CAP, STRIPE_PUBLISHABLE_KEY, planAmount, planKey, money, fnError } from '@/billingConfig'
import '@/assets/na-staff.css'

const blank = () => ({
  email: '', password: '', fullName: '', contactTitle: '', phone: '', website: '',
  businessName: '', orgType: ORG_TYPES[0], population: '', publicationName: '', businessDescription: '',
  address: { street: '', city: '', state: '', zip: '' },
  plan: 'community', billingInterval: 'month', customMonthly: null, paymentMethod: 'card',
  billingName: '', billingEmail: '', poNumber: '', languages: ['English'], additionalNotes: '',
})

export default {
  name: 'StaffOnboardCustomerView',
  components: { StaffHeader },
  data: () => ({
    CONTACT_ROLES, ORG_TYPES, POPULATIONS, PLANS, PLAN_LIST, TRIAL_DAYS, TRIAL_EMAIL_CAP,
    steps: ['Account', 'Contact', 'Organization', 'Address', 'Plan & billing', 'Review'],
    step: 0, maxStep: 0, f: blank(), showPwd: false, newLang: '',
    leadId: '', leadName: '',
    cardOpen: false, stripeLoading: false, cardComplete: false, savingCard: false, cardError: '',
    cardSaved: false, cardBrand: '', cardLast4: '', stripeCustomerId: '', stripePaymentMethodId: '', clientSecret: '',
    submitting: false, done: false, message: '', messageType: 'ok', error: '',
  }),
  computed: {
    strength() {
      const v = this.f.password || ''
      return [v.length >= 8, /[A-Z]/.test(v), /[0-9]/.test(v), /[^A-Za-z0-9]/.test(v)].filter(Boolean).length * 25
    },
    strengthLabel() { return ['None', 'Weak', 'Fair', 'Good', 'Strong'][this.strength / 25] },
    amount() { return planAmount(this.f.plan, this.f.billingInterval, Math.round((this.f.customMonthly || 0) * 100)) },
    setupFee() { return this.f.billingInterval === 'year' ? 0 : PLANS[this.f.plan].setup },
  },
  watch: { step(v) { this.maxStep = Math.max(this.maxStep, v) } },
  async mounted() {
    const id = this.$route.query.lead
    if (!id) return
    try {
      const s = await getDoc(doc(db, 'leads', String(id)))
      if (!s.exists()) return
      const l = s.data()
      this.leadId = s.id
      this.leadName = l.companyName || 'this lead'
      Object.assign(this.f, {
        email: l.email || '', fullName: l.contactName || '', contactTitle: l.contactRole || '', phone: l.phone || '',
        businessName: l.companyName || '', orgType: ORG_TYPES.includes(l.orgType) ? l.orgType : ORG_TYPES[0],
        population: l.population || '', plan: planKey(l.planInterest),
      })
      this.f.address.state = (l.state || '').toUpperCase().slice(0, 2)
    } catch (e) { console.warn('Lead prefill failed', e) }
  },
  beforeDestroy() { this.destroyCard() },
  methods: {
    money,
    canStep(i) {
      const f = this.f
      if (i === 0) return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email) && f.password.length >= 8
      if (i === 2) return !!f.businessName.trim()
      if (i === 3) return !!(f.address.street && f.address.city && f.address.state && f.address.zip)
      if (i === 4) return f.paymentMethod === 'card' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.billingEmail || f.email)
      return true
    },
    next() { if (this.canStep(this.step)) this.step++ },
    suggestPlan() {
      const p = POPULATIONS.find(x => x.label === this.f.population)
      if (p && p.plan) this.f.plan = planKey(p.plan)
    },
    genPassword() {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*?'
      const buf = new Uint32Array(14)
      crypto.getRandomValues(buf)
      this.f.password = Array.from(buf, n => chars[n % chars.length]).join('')
      this.showPwd = true
    },
    addLang() {
      const l = this.newLang.trim()
      if (l && !this.f.languages.includes(l)) this.f.languages.push(l)
      this.newLang = ''
    },

    // ── Stripe card ──
    async openCard() {
      this.cardOpen = true
      this.cardError = ''
      this.stripeLoading = true
      try {
        const [stripe, res] = await Promise.all([
          loadStripe(STRIPE_PUBLISHABLE_KEY),
          httpsCallable(functions, 'createStripeSetupIntent')({
            email: this.f.email, name: this.f.billingName || this.f.fullName, businessName: this.f.businessName,
            address: this.f.address.street ? { ...this.f.address, country: 'US' } : null,
          }),
        ])
        if (!stripe) throw new Error('Stripe failed to load')
        this._stripe = stripe
        this.stripeCustomerId = res.data.customerId
        this.clientSecret = res.data.clientSecret
        this._card = stripe.elements().create('card', {
          style: { base: { color: '#101B2D', fontFamily: '"Libre Franklin", Arial, sans-serif', fontSize: '16px', '::placeholder': { color: '#9AA1AC' } }, invalid: { color: '#A31F1F' } },
        })
        this._card.on('change', (e) => { this.cardComplete = e.complete; this.cardError = e.error ? e.error.message : '' })
        this.stripeLoading = false
        await this.$nextTick()
        this._card.mount('#ob-card')
      } catch (e) {
        console.error(e)
        this.cardError = 'Could not load the secure card form. ' + fnError(e)
        this.stripeLoading = false
      }
    },
    async saveCard() {
      if (!this._stripe || !this._card) return
      this.savingCard = true
      this.cardError = ''
      try {
        const { setupIntent, error } = await this._stripe.confirmCardSetup(this.clientSecret, {
          payment_method: { card: this._card, billing_details: { name: this.f.billingName || this.f.fullName || undefined, email: this.f.email || undefined } },
        })
        if (error) { this.cardError = error.message; return }
        const r = await httpsCallable(functions, 'confirmStripePaymentMethod')({ customerId: this.stripeCustomerId, paymentMethodId: setupIntent.payment_method })
        this.stripePaymentMethodId = setupIntent.payment_method
        this.cardBrand = (r.data.brand || 'card').replace(/^./, c => c.toUpperCase())
        this.cardLast4 = r.data.last4
        this.cardSaved = true
        this.destroyCard()
      } catch (e) {
        this.cardError = 'Could not save this card. ' + fnError(e)
      } finally { this.savingCard = false }
    },
    changeCard() {
      this.cardSaved = false
      this.stripePaymentMethodId = ''
      this.cardBrand = this.cardLast4 = ''
      this.openCard()
    },
    closeCard() { this.destroyCard(); this.cardOpen = false },
    destroyCard() {
      if (this._card) { try { this._card.destroy() } catch (e) { /* gone */ } }
      this._card = null
      this.cardOpen = false
      this.cardComplete = false
    },

    async submit() {
      this.error = ''
      this.message = ''
      for (const i of [0, 2, 3, 4]) if (!this.canStep(i)) { this.step = i; this.error = 'Please finish this step first.'; return }
      this.submitting = true
      try {
        const f = this.f
        const res = await httpsCallable(functions, 'createCustomerAccount')({
          ...f,
          address: { ...f.address, country: 'USA' },
          customMonthly: f.plan === 'city' && f.customMonthly ? Math.round(f.customMonthly * 100) : null,
          billingEmail: f.paymentMethod === 'invoice' ? (f.billingEmail || f.email) : f.email,
          stripeCustomerId: this.stripeCustomerId || null,
          stripePaymentMethodId: this.cardSaved ? this.stripePaymentMethodId : null,
          cardBrand: this.cardSaved ? this.cardBrand : null,
          cardLast4: this.cardSaved ? this.cardLast4 : null,
          leadId: this.leadId || null,
        })
        this.done = true
        if (res.data.emailSent === false) {
          this.messageType = 'warn'
          this.message = `Account created, but the welcome email didn't send (${res.data.emailError || 'unknown error'}). They can use "Forgot password?" on the sign-in page. Check that newsappify.com is verified in Resend.`
        } else {
          this.messageType = 'ok'
          this.message = `Account created for ${f.businessName}. ${f.email} has a welcome email with a link to set their password.`
        }
        setTimeout(() => this.$router.push(this.$route.query.lead ? '/staff-leads' : `/admin/customers/${res.data.companyId}`).catch(() => {}), 2500)
      } catch (e) {
        console.error(e)
        this.error = 'Could not create the account: ' + fnError(e)
      } finally { this.submitting = false }
    },
  },
}
</script>

<style scoped>
.lead-note { margin-bottom: 20px; }
.layout { display: grid; grid-template-columns: minmax(0, 1fr) 320px; gap: 24px; align-items: start; }
.side { position: sticky; top: 96px; display: flex; flex-direction: column; gap: 16px; }
.steps { list-style: none; display: flex; gap: 6px; margin: 0 0 26px; padding: 0 0 18px; border-bottom: 1px solid var(--na-rule); overflow-x: auto; }
.steps li { flex: 1; min-width: 92px; }
.steps button { width: 100%; display: flex; flex-direction: column; align-items: center; gap: 6px; border: 0; background: none; font-family: var(--na-sans); font-size: 0.82rem; font-weight: 600; color: var(--na-ink-2); cursor: pointer; }
.steps button:disabled { cursor: default; opacity: 0.6; }
.steps .n { width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid var(--na-rule); background: #fff; font-weight: 700; }
.steps li.on button { color: var(--na-navy); }
.steps li.on .n { border-color: var(--na-navy); color: var(--na-navy); box-shadow: 0 0 0 4px rgba(20, 49, 94, 0.12); }
.steps li.done .n { background: var(--na-navy); border-color: var(--na-navy); color: #fff; }
.panel { font-family: var(--na-serif); font-size: 1.45rem; margin: 0; }
.pass { display: flex; gap: 8px; align-items: center; }
.pass input { flex: 1; }
.plans { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.plan { display: flex; flex-direction: column; gap: 4px; align-items: flex-start; text-align: left; padding: 16px; border: 1.5px solid var(--na-rule); border-radius: 10px; background: #fff; cursor: pointer; font-family: var(--na-sans); color: var(--na-ink); }
.plan:hover { border-color: var(--na-navy); }
.plan.on { border-color: var(--na-navy); background: #F2F5FA; box-shadow: 0 0 0 3px rgba(20, 49, 94, 0.12); }
.plan b { font-family: var(--na-serif); font-size: 1.2rem; }
.plan .price { font-family: var(--na-serif); font-size: 1.35rem; font-weight: 700; color: var(--na-navy); }
.plan .price small { font-size: 0.8rem; color: var(--na-ink-2); font-weight: 400; }
.plan > small { font-size: 0.8rem; color: var(--na-ink-2); }
.summary { padding: 4px 16px; background: #FAF6EE; border: 1px solid var(--na-rule); border-radius: 10px; }
.card-prompt { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.card-el { padding: 13px 14px; border: 1.5px solid #DCD2BF; border-radius: 8px; background: #fff; }
.card-el.hidden { display: none; }
.card-saved { display: flex; justify-content: space-between; align-items: center; }
.row-end { display: flex; justify-content: flex-end; gap: 8px; }
.more summary { cursor: pointer; font-weight: 600; color: var(--na-navy); margin-bottom: 12px; }
.tags { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; padding: 8px; border: 1.5px solid #DCD2BF; border-radius: 8px; background: #fff; }
.tags input { flex: 1; min-width: 160px; border: 0 !important; min-height: 30px !important; padding: 2px 4px !important; box-shadow: none !important; }
.x { border: 0; background: none; cursor: pointer; color: inherit; font-size: 1rem; line-height: 1; }
.review { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.review > div { padding: 14px 16px; border: 1px solid var(--na-rule); border-radius: 10px; background: #FAF6EE; }
.review h4 { margin: 0 0 6px; font-size: 0.72rem; letter-spacing: 0.1em; text-transform: uppercase; color: var(--na-brass-text); }
.review p { margin: 0; line-height: 1.5; }
.foot { display: flex; align-items: center; gap: 10px; margin-top: 28px; padding-top: 18px; border-top: 1px solid var(--na-rule); }
.grow { flex: 1; }
.check { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
.check li { display: flex; align-items: center; gap: 10px; font-size: 0.95rem; color: var(--na-ink-2); }
.check li::before { content: ''; width: 20px; height: 20px; border-radius: 50%; border: 2px solid var(--na-rule); flex-shrink: 0; }
.check li.ok { color: var(--na-ink); }
.check li.ok::before { background: var(--na-navy) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3E%3Cpath d='M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z'/%3E%3C/svg%3E") center / 14px no-repeat; border-color: var(--na-navy); }
@media (max-width: 1000px) {
  .layout { grid-template-columns: 1fr; }
  .side { position: static; }
  .plans, .review { grid-template-columns: 1fr; }
}
</style>